/**
 * Tender lifecycle helpers shared by the SEO layer.
 *
 * Mirrors `CLOSED_STATUSES` in services/api/src/modules/catalog/catalog.service.ts
 * (the `status=closed` browse group). Kept as a separate constant rather than
 * imported because the API's value is typed against the Prisma enum, while the
 * catalog client models `status` as a plain string over the wire.
 */
export const CLOSED_STATUSES = ['closed', 'expired', 'archived'] as const;

/** True once bidding is over — no longer part of the browsable live catalog. */
export function isFinishedStatus(status: string | undefined | null): boolean {
  return !!status && (CLOSED_STATUSES as readonly string[]).includes(status);
}

/**
 * Should this tender page be indexable?
 *
 * A live lot always is — its value is the open deadline, and it has no winner yet
 * by definition. A finished lot is worth indexing only when it carries an outcome
 * (who won, for how much): that is genuinely unique content and the reason to keep
 * the archive at all. A finished lot with no outcome is a stale notice that is
 * near-identical to thousands of siblings — the scaled-content profile Google's
 * March-2024 policy targets — so it gets `noindex` rather than being silently
 * orphaned out of the sitemap.
 *
 * `scope === 'supply'` (cooperation.uz reverse-auction supply requests) is a hard
 * override, checked BEFORE the status/winner logic above: these are thin,
 * budgetless leads — the same crawl-budget caution that keeps them out of the
 * default browse scope (see catalog.tsx / catalog-client.ts) means their detail
 * pages must never be indexed either, live or awarded, winner or not.
 *
 * `outcomeLocked` counts as an outcome (2026-10-07): since the outcome wall the
 * API sends a guest — and therefore every PRERENDERED page — no `winnerName`,
 * only the flag that one exists. Keying on `winnerName` alone would flip every
 * awarded page to `noindex` the day the wall shipped, which is not a decision
 * the owner made. Whether a walled page still earns its place in the index is
 * the SEO watch item that decision does carry.
 *
 * `outcomeTransferred` is the second hard override (Igor, 2026-10-09): a lot
 * whose winner was COPIED from `tender_results` (etender, tender-mc,
 * cooperation — `syncResultOutcomes`) rather than harvested with the lot. That
 * copy exists for /results and the outcome wall, not to open ~4k pages at once
 * and 6–7k a month to search engines, so such a finished lot stays `noindex`
 * whatever outcome it shows. The API leaves the same lots out of the awarded
 * index (prerender + sitemap) and IndexNow. Lots that were indexable before —
 * prequest awards, KZ goszakup winners — never carry the flag.
 */
export function isTenderIndexable(tender: {
  status?: string | null;
  winnerName?: string | null;
  outcomeLocked?: boolean | null;
  outcomeTransferred?: boolean | null;
  scope?: string | null;
}): boolean {
  if (tender.scope === 'supply') return false;
  if (!isFinishedStatus(tender.status)) return true;
  if (tender.outcomeTransferred === true) return false;
  return Boolean(tender.winnerName) || tender.outcomeLocked === true;
}

/**
 * Bidding is over and NO outcome is published — the lot that gets the
 * «Приём заявок завершён … Итоги пока не опубликованы» note and the
 * «Похожие открытые лоты» block (ChatGPT audit 2026-10-08, item 1).
 *
 * "Over" is a finished status OR a deadline already in the past: the expiry
 * sweep runs once per ingestion cycle, so for up to ~2h a lot whose deadline
 * has gone still says `active`. The deadline half needs a clock, and a clock
 * read during prerender/SSR would bake an hours-old "now" into the HTML and
 * mismatch on hydration (React #418 — the reason the days-left badge is
 * client-only). So the caller passes `now` only once hydrated, and `null`
 * before that, which turns the deadline half off.
 *
 * "An outcome" is anything the page already renders an outcome block for — a
 * winner, the walled-winner flag, a final price, or «Закупка не состоялась».
 * Those pages are deliberately NOT touched by this: the outcome block (or the
 * wall from the 2026-10-07 release) is their honest status already.
 *
 * Indexing does not read this. `isTenderIndexable` stays the only rule, and
 * these lots are `noindex` (finished, no winner) exactly as before.
 */
export function isClosedWithoutOutcome(
  tender: {
    status?: string | null;
    deadline?: string | null;
    winnerName?: string | null;
    outcomeLocked?: boolean | null;
    finalPriceUzs?: string | null;
    outcomeStatus?: string | null;
    announcement?: boolean | null;
  },
  now: number | null,
): boolean {
  if (tender.announcement === true) return false;
  const deadlineMs = tender.deadline ? Date.parse(tender.deadline) : NaN;
  const over =
    isFinishedStatus(tender.status) ||
    (now != null && Number.isFinite(deadlineMs) && deadlineMs < now);
  if (!over) return false;
  const hasOutcome =
    Boolean(tender.winnerName) ||
    tender.outcomeLocked === true ||
    Boolean(tender.finalPriceUzs) ||
    tender.outcomeStatus === 'failed';
  return !hasOutcome;
}
